import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { TrimmedNonEmptyString } from "./baseSchemas";
import type { ProviderKind } from "./orchestration";

export const CODEX_REASONING_EFFORT_OPTIONS = [
  "ultra",
  "max",
  "xhigh",
  "high",
  "medium",
  "low",
] as const;
export type CodexReasoningEffort = (typeof CODEX_REASONING_EFFORT_OPTIONS)[number];
export const CLAUDE_CODE_EFFORT_OPTIONS = [
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
  "ultrathink",
] as const;
export type ClaudeCodeEffort = (typeof CLAUDE_CODE_EFFORT_OPTIONS)[number];
export type ProviderReasoningEffort = CodexReasoningEffort | ClaudeCodeEffort;

export const CODEX_DEFAULT_MODEL = "gpt-6-astra";
export const CODEX_SPARK_MODEL = "gpt-5.3-codex-spark";
export const CLAUDE_DEFAULT_MODEL = "claude-fable-5-1";

/**
 * Generic option value carried by ACP-based provider adapters (cursor, opencode).
 * Used to pass per-turn options like reasoning effort or thinking toggles to
 * the agent without baking them into model-specific schemas.
 *
 * Mirrors t3code's @t3tools/contracts ProviderOptionSelection — required by
 * the ported ACP runtime (apps/server/src/provider/acp/*) so cursor + opencode
 * adapters can carry user-selected reasoning levels through the session.
 */
export const ProviderOptionSelectionValue = Schema.Union([TrimmedNonEmptyString, Schema.Boolean]);
export type ProviderOptionSelectionValue = typeof ProviderOptionSelectionValue.Type;

export const ProviderOptionSelection = Schema.Struct({
  id: TrimmedNonEmptyString,
  value: ProviderOptionSelectionValue,
});
export type ProviderOptionSelection = typeof ProviderOptionSelection.Type;

/**
 * The object form on its own, exported so callers that need the individual
 * field schemas can reach them. The exported `CodexModelOptions` below is a
 * union and has no `.fields`.
 */
export const CodexModelOptionsObject = Schema.Struct({
  reasoningEffort: Schema.optional(Schema.Literals(CODEX_REASONING_EFFORT_OPTIONS)),
  fastMode: Schema.optional(Schema.Boolean),
});

/** The object form on its own; see {@link CodexModelOptionsObject}. */
export const ClaudeModelOptionsObject = Schema.Struct({
  thinking: Schema.optional(Schema.Boolean),
  effort: Schema.optional(Schema.Literals(CLAUDE_CODE_EFFORT_OPTIONS)),
  fastMode: Schema.optional(Schema.Boolean),
  contextWindow: Schema.optional(Schema.String),
});

/**
 * `[{ id, value }]` folded into `{ [id]: value }`.
 *
 * Duplicate ids keep the last, which is what an object literal would have done
 * had the sender used one.
 */
function selectionArrayToRecord(
  selections: ReadonlyArray<typeof ProviderOptionSelection.Type>,
): Record<string, string | boolean> {
  const record: Record<string, string | boolean> = {};
  for (const { id, value } of selections) {
    record[id] = value;
  }
  return record;
}

function recordToSelectionArray(
  value: unknown,
): ReadonlyArray<typeof ProviderOptionSelection.Type> {
  if (typeof value !== "object" || value === null) return [];
  return Object.entries(value as Record<string, unknown>).flatMap(([id, entry]) =>
    typeof entry === "string" || typeof entry === "boolean" ? [{ id, value: entry }] : [],
  );
}

/**
 * Also accept the option array that t3code-derived clients send.
 *
 * Forge's own surfaces send `{ reasoningEffort: "high" }`. The vendored mobile
 * client encodes the same choice as `[{ id: "reasoningEffort", value: "high" }]`,
 * its canonical shape, and the two contract packages never compare notes. So a
 * person picking a reasoning effort on the phone produced a `startTurn` payload
 * the server refused outright with "Expected object, got [...]" — not a dropped
 * option, a turn that never ran at all.
 *
 * Tolerated here rather than changed in the vendored encoder: this accepts both
 * shapes from any client, while `@t3tools/contracts` keeps the canonical form
 * it shares with upstream. The array is normalised into the object the provider
 * adapters already read, so nothing downstream learns a second shape.
 *
 * The object member stays first because it is what every Forge surface sends.
 * `Schema.Struct` rejects an array outright rather than decoding it to an empty
 * object, so an array falls through to the member built for it.
 */
export const CodexModelOptions = Schema.Union([
  CodexModelOptionsObject,
  Schema.Array(ProviderOptionSelection).pipe(
    Schema.decodeTo(
      CodexModelOptionsObject,
      SchemaTransformation.transformOrFail({
        decode: (selections) =>
          Effect.succeed(
            selectionArrayToRecord(selections) as typeof CodexModelOptionsObject.Encoded,
          ),
        encode: (value) => Effect.succeed(recordToSelectionArray(value)),
      }),
    ),
  ),
]);
export type CodexModelOptions = typeof CodexModelOptionsObject.Type;

export const ClaudeModelOptions = Schema.Union([
  ClaudeModelOptionsObject,
  Schema.Array(ProviderOptionSelection).pipe(
    Schema.decodeTo(
      ClaudeModelOptionsObject,
      SchemaTransformation.transformOrFail({
        decode: (selections) =>
          Effect.succeed(
            selectionArrayToRecord(selections) as typeof ClaudeModelOptionsObject.Encoded,
          ),
        encode: (value) => Effect.succeed(recordToSelectionArray(value)),
      }),
    ),
  ),
]);
export type ClaudeModelOptions = typeof ClaudeModelOptionsObject.Type;

export const ProviderModelOptions = Schema.Struct({
  codex: Schema.optional(CodexModelOptions),
  claudeAgent: Schema.optional(ClaudeModelOptions),
  ollama: Schema.optional(Schema.Struct({})),
  cmmd: Schema.optional(Schema.Struct({})),
  apex: Schema.optional(Schema.Struct({})),
  cursor: Schema.optional(Schema.Struct({})),
  opencode: Schema.optional(Schema.Struct({})),
});
export type ProviderModelOptions = typeof ProviderModelOptions.Type;

export const EffortOption = Schema.Struct({
  value: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  isDefault: Schema.optional(Schema.Boolean),
});
export type EffortOption = typeof EffortOption.Type;

export const ContextWindowOption = Schema.Struct({
  value: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  isDefault: Schema.optional(Schema.Boolean),
});
export type ContextWindowOption = typeof ContextWindowOption.Type;

export const ModelCapabilities = Schema.Struct({
  reasoningEffortLevels: Schema.Array(EffortOption),
  supportsFastMode: Schema.Boolean,
  supportsThinkingToggle: Schema.Boolean,
  contextWindowOptions: Schema.Array(ContextWindowOption),
  promptInjectedEffortLevels: Schema.Array(TrimmedNonEmptyString),
});
export type ModelCapabilities = typeof ModelCapabilities.Type;

export interface ProviderCatalogModel {
  readonly slug: string;
  readonly name: string;
  readonly isCustom: false;
  readonly isDefault?: boolean;
  readonly isLegacy?: boolean;
  readonly capabilities: ModelCapabilities;
}

function effortLabel(value: string): string {
  switch (value) {
    case "xhigh":
      return "Extra High";
    default:
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }
}

function modelCapabilities(input: {
  readonly reasoningEfforts: ReadonlyArray<string>;
  readonly defaultReasoningEffort?: string;
  readonly supportsFastMode: boolean;
  readonly supportsThinkingToggle?: boolean;
  readonly contextWindowOptions?: ModelCapabilities["contextWindowOptions"];
  readonly promptInjectedEffortLevels?: ReadonlyArray<string>;
}): ModelCapabilities {
  return {
    reasoningEffortLevels: input.reasoningEfforts.map((value) => ({
      value,
      label: effortLabel(value),
      ...(value === input.defaultReasoningEffort ? { isDefault: true } : {}),
    })),
    supportsFastMode: input.supportsFastMode,
    supportsThinkingToggle: input.supportsThinkingToggle ?? false,
    contextWindowOptions: input.contextWindowOptions ?? [],
    promptInjectedEffortLevels: [...(input.promptInjectedEffortLevels ?? [])],
  };
}

const CODEX_56_REASONING_EFFORTS = ["low", "medium", "high", "xhigh", "max", "ultra"] as const;
const CODEX_55_REASONING_EFFORTS = ["low", "medium", "high", "xhigh"] as const;
const CLAUDE_5_REASONING_EFFORTS = ["low", "medium", "high", "xhigh", "max", "ultrathink"] as const;
const CLAUDE_CONTEXT_WINDOWS = [
  { value: "200k", label: "200k", isDefault: true },
  { value: "1m", label: "1M" },
] as const;

export const EMPTY_MODEL_CAPABILITIES = modelCapabilities({
  reasoningEfforts: [],
  supportsFastMode: false,
});

/**
 * Last-known-good Codex catalog used before live app-server discovery completes
 * or when discovery is unavailable. The Codex app-server `model/list` response
 * replaces this fallback for healthy provider snapshots.
 */
export const CODEX_BUILT_IN_MODELS: ReadonlyArray<ProviderCatalogModel> = [
  {
    slug: CODEX_DEFAULT_MODEL,
    name: "GPT-6 Astra",
    isCustom: false,
    isDefault: true,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_56_REASONING_EFFORTS,
      defaultReasoningEffort: "medium",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.6-sol",
    name: "GPT-5.6 Sol",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_56_REASONING_EFFORTS,
      defaultReasoningEffort: "low",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.6-terra",
    name: "GPT-5.6 Terra",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_56_REASONING_EFFORTS,
      defaultReasoningEffort: "medium",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.6-luna",
    name: "GPT-5.6 Luna",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_56_REASONING_EFFORTS.filter((effort) => effort !== "ultra"),
      defaultReasoningEffort: "medium",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.5",
    name: "GPT-5.5",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_55_REASONING_EFFORTS,
      defaultReasoningEffort: "medium",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.4",
    name: "GPT-5.4",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_55_REASONING_EFFORTS,
      defaultReasoningEffort: "medium",
      supportsFastMode: true,
    }),
  },
  {
    slug: "gpt-5.4-mini",
    name: "GPT-5.4 Mini",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_55_REASONING_EFFORTS,
      defaultReasoningEffort: "medium",
      supportsFastMode: false,
    }),
  },
  {
    slug: CODEX_SPARK_MODEL,
    name: "GPT-5.3 Codex Spark",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CODEX_55_REASONING_EFFORTS,
      defaultReasoningEffort: "high",
      supportsFastMode: false,
    }),
  },
];

/**
 * Canonical Claude product catalog exposed by Forge. Claude Agent SDK
 * discovery can enrich capabilities for an exact canonical slug, but
 * ambiguous family aliases never narrow or replace this list.
 */
export const CLAUDE_BUILT_IN_MODELS: ReadonlyArray<ProviderCatalogModel> = [
  {
    slug: CLAUDE_DEFAULT_MODEL,
    name: "Claude Fable 5.1",
    isCustom: false,
    isDefault: true,
    capabilities: modelCapabilities({
      reasoningEfforts: ["low", "medium", "high", "xhigh", "max", "ultrathink"],
      defaultReasoningEffort: "high",
      supportsFastMode: false,
      promptInjectedEffortLevels: ["ultrathink"],
    }),
  },
  {
    slug: "claude-opus-5",
    name: "Claude Opus 5",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CLAUDE_5_REASONING_EFFORTS,
      defaultReasoningEffort: "high",
      supportsFastMode: true,
      contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
      promptInjectedEffortLevels: ["ultrathink"],
    }),
  },
  {
    slug: "claude-fable-5",
    name: "Claude Fable 5",
    isCustom: false,
    isLegacy: true,
    capabilities: modelCapabilities({
      reasoningEfforts: CLAUDE_5_REASONING_EFFORTS,
      defaultReasoningEffort: "high",
      supportsFastMode: false,
      contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
      promptInjectedEffortLevels: ["ultrathink"],
    }),
  },
  {
    slug: "claude-sonnet-5",
    name: "Claude Sonnet 5",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CLAUDE_5_REASONING_EFFORTS,
      defaultReasoningEffort: "high",
      supportsFastMode: false,
      promptInjectedEffortLevels: ["ultrathink"],
    }),
  },
  {
    slug: "claude-opus-4-8",
    name: "Claude Opus 4.8",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: CLAUDE_5_REASONING_EFFORTS,
      defaultReasoningEffort: "high",
      supportsFastMode: true,
      contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
      promptInjectedEffortLevels: ["ultrathink"],
    }),
  },
  {
    slug: "claude-haiku-4-5",
    name: "Claude Haiku 4.5",
    isCustom: false,
    capabilities: modelCapabilities({
      reasoningEfforts: [],
      supportsFastMode: false,
      supportsThinkingToggle: true,
    }),
  },
];

const CLAUDE_COMPATIBILITY_MODEL_CAPABILITIES: Readonly<Record<string, ModelCapabilities>> = {
  "claude-opus-4-7": modelCapabilities({
    reasoningEfforts: ["low", "medium", "high", "max", "ultrathink"],
    defaultReasoningEffort: "high",
    supportsFastMode: false,
    contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
    promptInjectedEffortLevels: ["ultrathink"],
  }),
  "claude-opus-4-6": modelCapabilities({
    reasoningEfforts: ["low", "medium", "high", "max", "ultrathink"],
    defaultReasoningEffort: "high",
    supportsFastMode: true,
    contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
    promptInjectedEffortLevels: ["ultrathink"],
  }),
  "claude-sonnet-4-6": modelCapabilities({
    reasoningEfforts: ["low", "medium", "high", "ultrathink"],
    defaultReasoningEffort: "high",
    supportsFastMode: false,
    contextWindowOptions: CLAUDE_CONTEXT_WINDOWS,
    promptInjectedEffortLevels: ["ultrathink"],
  }),
};

export function getProviderCatalogModel(
  provider: "codex" | "claudeAgent",
  model: string | null | undefined,
): ProviderCatalogModel | undefined {
  const slug = model?.trim().replace(/\[1m\]$/, "");
  if (!slug) {
    return undefined;
  }
  const catalog = provider === "codex" ? CODEX_BUILT_IN_MODELS : CLAUDE_BUILT_IN_MODELS;
  return catalog.find((candidate) => candidate.slug === slug);
}

export function getProviderModelCapabilities(
  provider: "codex" | "claudeAgent",
  model: string | null | undefined,
): ModelCapabilities | undefined {
  const slug = model?.trim().replace(/\[1m\]$/, "");
  if (!slug) {
    return undefined;
  }
  const compatibilityCapabilities =
    provider === "claudeAgent" &&
    Object.prototype.hasOwnProperty.call(CLAUDE_COMPATIBILITY_MODEL_CAPABILITIES, slug)
      ? CLAUDE_COMPATIBILITY_MODEL_CAPABILITIES[slug]
      : undefined;
  return getProviderCatalogModel(provider, slug)?.capabilities ?? compatibilityCapabilities;
}

export const DEFAULT_MODEL_BY_PROVIDER: Record<ProviderKind, string> = {
  codex: CODEX_DEFAULT_MODEL,
  claudeAgent: CLAUDE_DEFAULT_MODEL,
  ollama: "llama3.2",
  cmmd: "apex-flash",
  apex: "apex-flash",
  cursor: "cursor-default",
  opencode: "opencode-default",
};

export const DEFAULT_MODEL = DEFAULT_MODEL_BY_PROVIDER.codex;

/** Per-provider text generation model defaults. */
export const DEFAULT_GIT_TEXT_GENERATION_MODEL_BY_PROVIDER: Record<ProviderKind, string> = {
  codex: "gpt-5.4-mini",
  claudeAgent: "claude-haiku-4-5",
  ollama: "llama3.2",
  cmmd: "apex-flash",
  apex: "apex-flash",
  cursor: "cursor-default",
  opencode: "opencode-default",
};

export const MODEL_SLUG_ALIASES_BY_PROVIDER: Record<ProviderKind, Record<string, string>> = {
  codex: {
    "gpt-5-codex": "gpt-5.6-sol",
    "5.6": "gpt-5.6-sol",
    "gpt-5.6": "gpt-5.6-sol",
    "gpt-5.6-codex": "gpt-5.6-sol",
    sol: "gpt-5.6-sol",
    "5.6-sol": "gpt-5.6-sol",
    terra: "gpt-5.6-terra",
    "5.6-terra": "gpt-5.6-terra",
    luna: "gpt-5.6-luna",
    "5.6-luna": "gpt-5.6-luna",
    "5.4": "gpt-5.4",
    "5.5": "gpt-5.5",
    "gpt-5.5-codex": "gpt-5.5",
    "5.3": "gpt-5.3-codex",
    "gpt-5.3": "gpt-5.3-codex",
    "5.3-spark": CODEX_SPARK_MODEL,
    "gpt-5.3-spark": CODEX_SPARK_MODEL,
  },
  claudeAgent: {
    opus: "claude-opus-5",
    "opus-5": "claude-opus-5",
    "claude-opus-5": "claude-opus-5",
    fable: CLAUDE_DEFAULT_MODEL,
    "fable-5": "claude-fable-5",
    sonnet: "claude-sonnet-5",
    "sonnet-5": "claude-sonnet-5",
    "claude-sonnet-5": "claude-sonnet-5",
    "opus-4.8": "claude-opus-4-8",
    "claude-opus-4.8": "claude-opus-4-8",
    "opus-4.7": "claude-opus-4-7",
    "claude-opus-4.7": "claude-opus-4-7",
    "claude-opus-4-7-20260416": "claude-opus-4-7",
    "opus-4.6": "claude-opus-4-6",
    "claude-opus-4.6": "claude-opus-4-6",
    "claude-opus-4-6-20251117": "claude-opus-4-6",
    "sonnet-4.6": "claude-sonnet-4-6",
    "claude-sonnet-4.6": "claude-sonnet-4-6",
    "claude-sonnet-4-6-20251117": "claude-sonnet-4-6",
    haiku: "claude-haiku-4-5",
    "haiku-4.5": "claude-haiku-4-5",
    "claude-haiku-4.5": "claude-haiku-4-5",
    "claude-haiku-4-5-20251001": "claude-haiku-4-5",
  },
  ollama: {},
  cmmd: {},
  apex: {},
  cursor: {},
  opencode: {},
};

// ── Provider display names ────────────────────────────────────────────

export const PROVIDER_DISPLAY_NAMES: Record<ProviderKind, string> = {
  codex: "Codex",
  claudeAgent: "Claude",
  ollama: "Ollama",
  cmmd: "CMMD",
  apex: "CMMD",
  cursor: "Cursor",
  opencode: "OpenCode",
};

/**
 * The ACCOUNT brand a user signs in with — not always the agent's name.
 *
 * You connect a **ChatGPT** subscription to run the **Codex** agent, so every
 * personal sign-in surface must say "ChatGPT" where PROVIDER_DISPLAY_NAMES says
 * "Codex". That single divergence is why five auth call sites each grew their
 * own `provider === "claudeAgent" ? "Claude" : "ChatGPT"` ternary instead of
 * importing the shared map — and a binary ternary over a seven-member union
 * silently names five of them wrong.
 *
 * A Record over ProviderKind rather than a switch or a ternary: adding a
 * provider is then a compile error here, which is the same guarantee that
 * turned the `"claude"` vs `"claudeAgent"` drift (#2103/#2105) from a sentence
 * that read wrong in production into something the type checker catches.
 */
export const PROVIDER_ACCOUNT_NAMES: Record<ProviderKind, string> = {
  codex: "ChatGPT",
  claudeAgent: "Claude",
  ollama: "Ollama",
  cmmd: "CMMD",
  apex: "CMMD",
  cursor: "Cursor",
  opencode: "OpenCode",
};

/**
 * Providers a user can connect a PERSONAL subscription for.
 *
 * Load-bearing in three places that each hardcoded the same pair: the sign-in
 * gate, the auth-failure copy (naming a subscription a provider does not sell —
 * "no Ollama subscription connected" — is worse than staying generic), and the
 * credential harvest, which must never fall through to the Codex store for a
 * provider it was not told about.
 */
const PERSONAL_SUBSCRIPTION_PROVIDERS: ReadonlySet<ProviderKind> = new Set<ProviderKind>([
  "codex",
  "claudeAgent",
]);

export function supportsPersonalSubscription(
  provider: ProviderKind | undefined,
): provider is "codex" | "claudeAgent" {
  return provider !== undefined && PERSONAL_SUBSCRIPTION_PROVIDERS.has(provider);
}
